import { cssClass } from '../../style';
import { DESCRIPTION_MAX_LENGTH, NAME_MAX_LENGTH } from '../constants/lensconstants';
import { LensesTAGHandler } from '../api/LensTAG';
import { LocationInput } from './LocationInput';
import { Messages } from '../../codegen/Localize';
import { SiteUrls } from '@tableau/shared-urls/lib/src/SharedUrls';
import { SpaceInfo } from '../../app/state/SpaceInfo';
import { WindowLocationService } from '../../commonInfra/services/WindowLocationService';
import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  ButtonWidget,
  DialogType,
  DialogWidget,
  TabStyles,
  TextAreaWidget,
  TextFieldWidget,
} from '@tableau/widgets';
import React from 'react';
import { CloseAndDismiss, ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { GetLensNamesList, projectToLensMapType } from '~src/lens/utils/GetLensNamesList';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { IDataSourceDetailInfo } from '~src/ts/models/IDataSourceInfo';
import { isIProjectItemInfo } from '~src/ts/models/resourceInfoParts';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { Telemetry } from '~commonInfra/services/Telemetry';
import { LensOperationEvent, TelemetryEvents } from '~common/telemetryEvents/TelemetryEvents';

export const createLensDialog = (siteUrlName: string, spaceInfo: SpaceInfo, urls: SiteUrls, publishedDatasourceId: number) =>
  (closeAndDismiss: CloseAndDismiss<undefined>): React.ReactElement => {
  const dialogProps = ReactModalService.getDefaultDialogProps({
    type:DialogType.NormalWithTitleBar,
    title:Messages.lensCurationDialogTitle(),
    showCloseButton: true,
    testId: 'lens-creation-dialog',
  }, closeAndDismiss);
  const {close, dismiss} = closeAndDismiss;
  return <DialogWidget{...dialogProps}>
    <LensDialogContent {...{urls, publishedDatasourceId, siteUrlName, spaceInfo, close}}/>
   </DialogWidget>;
};

type LensDialogContentProps = {
  urls: SiteUrls;
  siteUrlName: string;
  publishedDatasourceId: number;
  spaceInfo: SpaceInfo;
  close: () => void;
};

const LensDialogContent = (props: LensDialogContentProps): JSX.Element => {
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [lensNames, setLensNames] = React.useState<projectToLensMapType>({});
  const ZeroWidthSpace = '\u200B';

  let initialProjectId = '';
  if (isIProjectItemInfo(props.spaceInfo.item)) {
    initialProjectId = props.spaceInfo.item.project.id;
  } else if (props.spaceInfo.spaceType === 'project') {
    // when create lens is under project space, we use the current project as the default selection
    initialProjectId = props.spaceInfo.item.id;
  }
  const [selectedProjectId, selectProjectId] = React.useState<string>(initialProjectId);
  const [isPublishing, setIsPublishing] = React.useState(false);

  const getNamesEffect = () => {
    GetLensNamesList().then(list => {
      setLensNames(list);
    });
  };

  const isNameUnique = !lensNames[selectedProjectId]?.includes(name);
  React.useEffect(getNamesEffect, []);

  const publish = async () => {
    setIsPublishing(true);
    try {
      const lensResponse = await LensesTAGHandler.createLens({
        name,
        description,
        site_id: parseInt(getSessionManagementServiceInstance().sessionSiteId()),
        project_id: parseInt(selectedProjectId),
        owner_id: parseInt(getSessionManagementServiceInstance().sessionUserId()),
        datasource_id: getPublishedDatasourceId(),
      });

      const lensUrl = props.urls.lensUrl(lensResponse.id);

      WindowLocationService.open(lensUrl, '_blank');

      props.close();
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: Messages.lensPublish_Toast_Success({LENS_NAME: name})
      });

      sendCreateLensTelemetryEvent(lensResponse.id, lensResponse.luid);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('failed to create lens', e);
      ToasterService.instance.pop({ type: ToastType.Error, text: Messages.lensPublish_Toast_Error()});
      setIsPublishing(false);
    }
  };

  const sendCreateLensTelemetryEvent = (lensId: string, lensLuid: string) => {
    Telemetry.sendEvent<LensOperationEvent>({
      type: TelemetryEvents.LensOperations,
      lens_id: lensId,
      lens_luid: lensLuid,
      action_type: 'Create',
      site_luid: getSessionManagementServiceInstance().sessionSiteLuid(),
      datasource_id: getPublishedDatasourceId().toString(),
      datasource_luid: getPublishedDatasourceLuid(),
      site_role: getSessionManagementServiceInstance().sessionSiteRole(),
    });
  };

  const getPublishedDatasourceId = (): number =>{
    let datasourceId = props.publishedDatasourceId;
    if (!datasourceId) {
      // we cant use item.id directly because we actually want the published datasource id.
      // currently we can use the idForFavoriting. But in long term we should switch this over to a more elegant method
      const datasourceInfo: IDataSourceDetailInfo = props.spaceInfo.item as IDataSourceDetailInfo;
      datasourceId = datasourceInfo ? parseInt(datasourceInfo.idForFavoriting) : 0;
    }

    return datasourceId;
  };

  const getPublishedDatasourceLuid = (): string =>{
    const datasourceInfo: IDataSourceDetailInfo = props.spaceInfo.item as IDataSourceDetailInfo;
    return datasourceInfo ? (datasourceInfo.luid ? datasourceInfo.luid : datasourceInfo.datasourceLuid) : '';
  };

  return <section className={contentStyle}>
    <aside>
      {Messages.lensCurationDialogCaption() + ' '}
      <a href={HelpService.getServerHelpUrlByCategory('ask-data-lenses')}>{Messages.learnMore()}</a>
    </aside>
    <TextFieldWidget
      label={Messages.name()}
      type='text'
      value={name}
      onChange={e => setName(e.target.value)}
      message={isNameUnique ? ZeroWidthSpace : Messages.lensPublish_UniqueName_Warning()}
      valid= {isNameUnique ? undefined : false}
      className={fullWidthInput}
      testId='curation-dialog-name'
      maxLength={NAME_MAX_LENGTH}
    />
    <TextAreaWidget
      label={Messages.description()}
      rows={2}
      value={description}
      onChange={e => setDescription(e.target.value)}
      className={fullWidthInput}
      testId='curation-dialog-description'
      maxLength={DESCRIPTION_MAX_LENGTH}
    />
    <div className={fullWidthInput}>
      <LocationInput
        selectedProjectId={selectedProjectId}
        selectProject={selectProjectId}
      />
    </div>
    <footer className={buttonsStyle}>
      <ButtonWidget
        testId={'curation-dialog-cancel'}
        onClick={props.close}
      >{Messages.cancel()}</ButtonWidget>
      <ButtonWidget
        buttonType={ButtonType.Primary}
        testId={'curation-dialog-publish'}
        onClick={isPublishing ? () => null : publish}
        disabled={!selectedProjectId || !name || !getPublishedDatasourceId()}
      >{isPublishing ?
        <ActivitySpinnerWidget
          testId={'lens-creation-dialog-submit'}
          spinnerSize={ActivitySpinnerSize.Small}
          onDarkBackground
          />:
        Messages.lensCurationPublish()}
      </ButtonWidget>
    </footer>
  </section>;
};

const cssPrefix = 'LensCurationDialog';
export const contentStyle = cssClass(cssPrefix, 'content', {
  color: TabStyles.Typography.Color.Secondary,
});

export const fullWidthInput = cssClass(cssPrefix, 'fullWidthText', {
  width: '100%',
  display: 'block',
  paddingTop: TabStyles.Spacing.MediumSpace,
  $nest: {
    'input': {
      width: '100%',
      resize: 'none',
    },
    'textarea': {
      width: '100%',
      resize: 'none',
    },
    label: {
      color: TabStyles.Typography.Color.Secondary,
    },
  },
});

export const buttonsStyle = cssClass(cssPrefix, 'footer', {
  display: 'flex',
  justifyContent: 'flex-end',
  $nest: {
    'button': {
      marginLeft: TabStyles.Spacing.MediumSpace,
      marginTop: TabStyles.Spacing.LargeSpace,
    },
  },
});
